import { inject, Injectable } from "@angular/core";
import { DataService as CoreDataService, PersistMode, Query, QueryColumn, QueryColumnSortOrder, QueryObjectsModel } from "@barthauer/abc/core/data";
import { Person, ROLES } from "@barthauer/custom/xmt-models";
import { concatMap, map, Observable, tap } from "rxjs";
import { DataService, ObjectKeys } from "../services/data.service";
import { ProjectPersonViewModel } from "../models/ProjectPerson.viewModel";
import { PersonStatus } from "../models/enumeration-list.model";

@Injectable()
export class HomeService{
  private readonly coreDataService = inject(CoreDataService);
  private readonly dataService = inject(DataService);

  public associatedMembers: Person[] = []

  public getAllMembers():Observable<Person[]>{
    const columns: QueryColumn[]=[
      this.coreDataService.createQueryColumn("Id"),
      this.coreDataService.createQueryColumn("ref:Id", "Name", QueryColumnSortOrder.Ascending),
    ]
    const opath =`Active <> 0 AND Exists(AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}' AND Position <> '${ROLES.ADMIN}' AND Status <> '${PersonStatus.ARCHIVED}'])`
    return this.dataService.readObjects(ObjectKeys.PERSON,columns,opath);
  }
  public getAssociatedMembers(projectId:string):Observable<Person[]>{
    const personQuery: Query = new Query();
    personQuery.ObjectType = ObjectKeys.PERSON;
    personQuery.Columns = [
      this.coreDataService.createQueryColumn("Id"),
      this.coreDataService.createQueryColumn("ref:Id", "Name", QueryColumnSortOrder.Ascending),
    ];
    personQuery.OPath = `Exists(ProjectPersons[ParentId='${projectId}']) AND Exists(AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}' AND Position <> '${ROLES.ADMIN}'])`;

    const projectPersonQuery: Query = new Query();
    projectPersonQuery.Name = "ProjectPerson";
    projectPersonQuery.ObjectType = ObjectKeys.PROJECT_PERSON;
    projectPersonQuery.OPath = `ProjectPersons[ParentId='${projectId}']`;
    projectPersonQuery.Columns = [
      this.coreDataService.createQueryColumn("Id"),
    ];

    personQuery.ObjectQueries = [projectPersonQuery];

    const query: QueryObjectsModel = new QueryObjectsModel();
    query.ObjectQueries = [personQuery];
    return this.coreDataService.executeReadObjectsQuery(query).pipe(
      map((persons)=>
        Array.isArray(persons) ? persons.map((person)=>{return {...person,ProjectPerson: person.ProjectPerson[0]}}) : []
      )
    )
  }
  public manageProjectMembers(projectId: string, newAssociatedMembersIds: string[], newDissociatedMembersIds: string[]):Observable<ArrayBuffer> {
    return this.getAdmins(projectId).pipe(
      map(Ids=>{
        newAssociatedMembersIds=[...newAssociatedMembersIds,...Ids];
        return [
          this.prepareCreateQuery(projectId,newAssociatedMembersIds),
          this.prepareDeleteQuery(newDissociatedMembersIds)
        ].filter(q => q);
      }),
      concatMap((query)=>this.dataService.persistMultipleObjects(query))  
    )
  }
  private prepareCreateQuery(projectId:string,newAssociatedMembersIds:string[]):
    { 
      objectKey: ObjectKeys; 
      persistMode: PersistMode; 
      data: Partial<ProjectPersonViewModel>[]
    }
  {
    if(newAssociatedMembersIds.length===0) return null;
    const objectKey=ObjectKeys.PROJECT_PERSON;
    const projectPersonData: Partial<ProjectPersonViewModel>[] = [];
    newAssociatedMembersIds.forEach(personId=>{
      projectPersonData.push(
        {
          IdRefPerson: personId,
          ParentId:projectId
        }
      )
    });
    return { objectKey: objectKey, persistMode: PersistMode.Insert, data: projectPersonData };
  }
  private prepareDeleteQuery(newDissociatedMembersIds:string[]):
    { 
      objectKey: ObjectKeys; 
      persistMode: PersistMode; 
      data: Partial<ProjectPersonViewModel>[]
    }
  {
    if(newDissociatedMembersIds.length===0) return null;
    const projectPersonData: Partial<ProjectPersonViewModel>[] = [];
    newDissociatedMembersIds.forEach(projectPersonId=>{
      projectPersonData.push(
        {
          Id: projectPersonId,
        }
      )
    });
    return { objectKey: ObjectKeys.PROJECT_PERSON, persistMode: PersistMode.Delete, data: projectPersonData };
  }
  private getAdmins(projectId:string):Observable<string[]>{
    const opath=`Not(Exists(ProjectPersons[ParentId='${projectId}'])) AND Exists(AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem("profileAddressId")}' AND Position = '${ROLES.ADMIN}' AND Status <> '${PersonStatus.ARCHIVED}']) AND Active <> 0`
    return this.dataService.readObjects(ObjectKeys.PERSON,[this.coreDataService.createQueryColumn("Id"),this.coreDataService.createQueryColumn("ref:Id","Name")],opath)
      .pipe(
        map((result)=>Array.isArray(result) ? result.map(person=>person.Id) : [])
      ) as unknown as Observable<string[]>
  }
}